import { type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

interface PreviewCardShellProps {
  className?: string
  icon: ReactNode
  title: ReactNode
  /** Attribution row — "<source> · <time ago>". Omitted when the item has no source. */
  source?: ReactNode
  /**
   * Draws the elbow tying the row back to the title's icon. Off when the row
   * names no origin — a connector to nothing reads as a rendering bug.
   */
  sourceConnected?: boolean
  /**
   * Keeps the title's line breaks instead of the one-line clamp. For cards
   * whose title IS the item's whole body (reminders) — collapsing it would
   * throw away the only content the card has. Still clamped, just taller.
   */
  multilineTitle?: boolean
  /** Body slot, between the source row and the footer. */
  children?: ReactNode
  /** Failure message, below the body and above the footer. Outside the
   *  click-through so reading the error can't navigate away from it. */
  error?: ReactNode
  /** Thumbs cluster. Absent when the item has no LLM message to rate. */
  feedback?: ReactNode
  /**
   * Right-aligned footer controls — light pill CTAs only (Figma 8478:20194).
   * Primary and destructive moves live on the cell's hover button cluster, not
   * here: the card describes the item, the cell acts on it.
   */
  actions?: ReactNode
  /** Click-through on the card's body, matching a click on the row itself. */
  onOpen?: () => void
}

/** Layout shared by every hover preview card: header, source, body, action footer. */
export function PreviewCardShell({
  className,
  icon,
  title,
  source,
  sourceConnected = true,
  multilineTitle = false,
  children,
  error,
  feedback,
  actions,
  onOpen,
}: PreviewCardShellProps) {
  return (
    <div className={twMerge('flex flex-col gap-4', className)}>
      {/* Everything above the footer is the click-through, mirroring a click on
          the row. Not a button: it wraps a markdown body that can hold its own
          links, and the same view is already keyboard-reachable from the row. */}
      <div
        className={twMerge('flex flex-col gap-1.5', typeof onOpen === 'function' && 'cursor-pointer')}
        onClick={onOpen}
      >
        {/* items-start when multiline so the icon rides the first line, not the block's middle. */}
        <div className={twMerge('flex items-center gap-2', multilineTitle && 'items-start')}>
          <div className="text-foreground-secondary inline-flex size-5 shrink-0 items-center justify-center">
            {icon}
          </div>
          {/* One line, per Figma — the card summarizes, the full view carries the rest. */}
          <h3
            className={twMerge(
              'text-foreground-secondary min-w-0 flex-1 text-sm font-medium',
              multilineTitle ? 'line-clamp-6 whitespace-pre-wrap break-words' : 'truncate',
            )}
          >
            {title}
          </h3>
        </div>

        {/* Hangs off the icon with a small elbow, so the origin reads as
            attribution for the title rather than as a second heading (Figma). */}
        {source && (
          <div className={twMerge('flex items-center gap-2')}>
            <div className="relative w-5">
              {sourceConnected && (
                <span
                  aria-hidden
                  className="corner-smooth absolute bottom-0 left-1/2 top-0 mb-1 min-h-2.5 w-1.5 shrink-0 -translate-y-3/4 rounded-bl-[6px] border-b border-l"
                />
              )}
            </div>

            <div className="min-w-0 flex-1">{source}</div>
          </div>
        )}
      </div>

      {typeof children !== 'undefined' && (
        <div
          className={twMerge('flex flex-col gap-2', typeof onOpen === 'function' && 'cursor-pointer')}
          onClick={onOpen}
        >
          {children}
        </div>
      )}

      {error}

      {/* An empty row still takes the column's gap-4, which shows as extra bottom padding. */}
      {(feedback || actions) && (
        <div className="flex items-center gap-2">
          {feedback}
          {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
        </div>
      )}
    </div>
  )
}
